import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { masrafAPI, caseFileAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import MasrafForm from '../components/Masraflar/MasrafForm';
import Icon from '../components/Common/Icon';
import './Masraflar.css';
import { onayla } from '../utils/onayla';

const Masraflar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [masraflar, setMasraflar] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMasraf, setEditingMasraf] = useState(null);
  const [selectedCaseFileId, setSelectedCaseFileId] = useState(null);
  const [activeTab, setActiveTab] = useState('gelir'); // 'gelir' | 'gider'
  const [sortColumn, setSortColumn] = useState('tarih');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc'

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [masraflarData, caseFilesData] = await Promise.all([
        masrafAPI.getAll(),
        caseFileAPI.getAll()
      ]);
      setMasraflar(masraflarData);
      setCaseFiles(caseFilesData);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      alert('Veri yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingMasraf(null);
    setSelectedCaseFileId(null);
    setShowForm(true);
  };

  const handleEdit = (masraf) => {
    setEditingMasraf(masraf);
    setSelectedCaseFileId(masraf.caseFileId || masraf.CaseFileId);
    setShowForm(true);
  };

  const handleDelete = async (masrafId) => {
    if (!await onayla('Bu masrafı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await masrafAPI.delete(masrafId);
      await loadData();
      alert('Masraf başarıyla silindi');
    } catch (error) {
      console.error('Masraf silinirken hata:', error);
      alert('Masraf silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleFormSubmit = async () => {
    await loadData();
    setShowForm(false);
    setEditingMasraf(null);
    setSelectedCaseFileId(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingMasraf(null);
    setSelectedCaseFileId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const getCaseFileName = (caseFileId) => {
    const caseFile = caseFiles.find(cf => cf.id === caseFileId || cf.Id === caseFileId);
    if (!caseFile) return `Dosya #${caseFileId}`;
    const foyNo = caseFile.foyNo || caseFile.FoyNo;
    const muvekkil = caseFile.muvekkil || caseFile.Muvekkil;
    return `Föy No: ${foyNo} - ${muvekkil}`;
  };

  // Avukatların göremeyeceği masraf türleri (karşılaştırma için normalize: İ -> I)
  const AVUKAT_GORMEYECEK_MASRAF_TURLERI = [
    'BERİLE VERİLEN',
    'REFERANSA VERİLEN AVANS',
    'MÜVEKKİLE YAPILAN ÖDEME',
    'MÜVEKKİLE YAPILAN ÖDEME(BAŞVURU AŞAMASI)',
    'MÜVEKKİLE YAPILAN ÖDEME(İCRA)',
    'MÜVEKKİLE YAPILAN ÖDEME(TAHKİM AŞAMASI)'
  ];

  const normalizeTurForCompare = (str) => {
    return String(str || '').trim().replace(/İ/g, 'I').toUpperCase();
  };

  const isAvukatGoremeyecekTur = (m) => {
    const tur = normalizeTurForCompare(m.MasrafTuru || m.masrafTuru);
    if (!tur) return false;
    return AVUKAT_GORMEYECEK_MASRAF_TURLERI.some(hidden => {
      const hiddenNorm = normalizeTurForCompare(hidden);
      return tur === hiddenNorm || tur.startsWith(hiddenNorm + '(') || tur.startsWith(hiddenNorm + ' ');
    });
  };

  // Sadece yeni yapı masrafları (MasrafTuru veya customMasrafTuru olanlar); avukat için belirli türler gizlenir
  let yeniMasraflar = masraflar.filter(m => (m.MasrafTuru || m.masrafTuru || m.CustomMasrafTuru || m.customMasrafTuru));
  if (!isAdmin()) {
    yeniMasraflar = yeniMasraflar.filter(m => !isAvukatGoremeyecekTur(m));
  }
  const gelirler = yeniMasraflar.filter(m => m.IsGelir || m.isGelir);
  const giderler = yeniMasraflar.filter(m => !(m.IsGelir || m.isGelir));

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortMasraflar = (list) => {
    if (!list.length) return list;
    const sorted = [...list];
    const dir = sortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let va, vb;
      switch (sortColumn) {
        case 'dosya':
          va = getCaseFileName(a.caseFileId || a.CaseFileId);
          vb = getCaseFileName(b.caseFileId || b.CaseFileId);
          return dir * (String(va).localeCompare(String(vb), 'tr'));
        case 'masrafTuru':
          va = a.MasrafTuru || a.masrafTuru || '';
          vb = b.MasrafTuru || b.masrafTuru || '';
          return dir * (String(va).localeCompare(String(vb), 'tr'));
        case 'tutar':
          va = parseFloat(a.Tutar ?? a.tutar) || 0;
          vb = parseFloat(b.Tutar ?? b.tutar) || 0;
          return dir * (va - vb);
        case 'tarih':
          va = a.Tarih || a.tarih ? new Date(a.Tarih || a.tarih).getTime() : 0;
          vb = b.Tarih || b.tarih ? new Date(b.Tarih || b.tarih).getTime() : 0;
          return dir * (va - vb);
        case 'aciklama':
          va = a.Aciklama || a.aciklama || '';
          vb = b.Aciklama || b.aciklama || '';
          return dir * (String(va).localeCompare(String(vb), 'tr'));
        default:
          return 0;
      }
    });
    return sorted;
  };

  const sortedGelirler = useMemo(() => sortMasraflar(gelirler), [gelirler, sortColumn, sortDirection, caseFiles]);
  const sortedGiderler = useMemo(() => sortMasraflar(giderler), [giderler, sortColumn, sortDirection, caseFiles]);

  const SortHeader = ({ column, children }) => (
    <th
      className="masraflar-table-sortable"
      onClick={() => handleSort(column)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleSort(column)}
    >
      <span className="masraflar-th-content">
        {children}
        <span className="masraflar-sort-icons">
          <span className={sortColumn === column && sortDirection === 'asc' ? 'active' : ''}>▲</span>
          <span className={sortColumn === column && sortDirection === 'desc' ? 'active' : ''}>▼</span>
        </span>
      </span>
    </th>
  );

  if (showForm) {
    return (
      <MasrafForm
        masraf={editingMasraf}
        caseFileId={selectedCaseFileId}
        caseFiles={caseFiles}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
      />
    );
  }

  if (loading) {
    return (
      <div className="masraflar-container">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="masraflar-container">
      <div className="page-header">
        <h1>Masraflar</h1>
        <div className="page-header-actions">
          {isAdmin() ? (
            <button className="btn btn-primary" onClick={handleCreate}>
              + Yeni Masraf Ekle
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleCreate}>
              + Masraf Talebi Oluştur
            </button>
          )}
        </div>
      </div>

      {/* Gelir / Gider Sekmeleri */}
      <div className="masraf-tabs">
        <button
          className={`masraf-tab ${activeTab === 'gelir' ? 'active' : ''}`}
          onClick={() => setActiveTab('gelir')}
        >
          <Icon name="income" size={17} /> Gelirler {gelirler.length > 0 && <span className="tab-badge">{gelirler.length}</span>}
        </button>
        <button
          className={`masraf-tab ${activeTab === 'gider' ? 'active' : ''}`}
          onClick={() => setActiveTab('gider')}
        >
          <Icon name="expense" size={17} /> Giderler {giderler.length > 0 && <span className="tab-badge">{giderler.length}</span>}
        </button>
      </div>

      {/* Gelirler Sekmesi */}
      {activeTab === 'gelir' && (
        <div className="masraf-section">
          <div className="masraflar-table-container">
            {gelirler.length === 0 ? (
              <div className="no-data">Henüz gelir kaydı bulunmamaktadır.</div>
            ) : (
              <table className="masraflar-table">
                <colgroup>
                  <col className="col-dosya" />
                  <col className="col-masraf-turu" />
                  <col className="col-tutar" />
                  <col className="col-tarih" />
                  <col className="col-aciklama" />
                  <col className="col-islemler" />
                </colgroup>
                <thead>
                  <tr>
                    <SortHeader column="dosya">Dosya</SortHeader>
                    <SortHeader column="masrafTuru">Masraf Türü</SortHeader>
                    <SortHeader column="tutar">Tutar</SortHeader>
                    <SortHeader column="tarih">Tarih</SortHeader>
                    <SortHeader column="aciklama">Açıklama</SortHeader>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGelirler.map((masraf) => (
                    <tr key={masraf.id || masraf.Id}>
                      <td>
                        <button
                          className="link-button"
                          onClick={() => navigate(`/case-files/${masraf.caseFileId || masraf.CaseFileId}`)}
                        >
                          {getCaseFileName(masraf.caseFileId || masraf.CaseFileId)}
                        </button>
                      </td>
                      <td>{masraf.MasrafTuru || masraf.masrafTuru || '-'}</td>
                      <td>{formatCurrency(masraf.Tutar || masraf.tutar)}</td>
                      <td>{formatDate(masraf.Tarih || masraf.tarih)}</td>
                      <td>{masraf.Aciklama || masraf.aciklama || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          {isAdmin() && (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEdit(masraf)}
                              >
                                Düzenle
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(masraf.id || masraf.Id)}
                              >
                                Sil
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Giderler Sekmesi */}
      {activeTab === 'gider' && (
        <div className="masraf-section">
          <div className="masraflar-table-container">
            {giderler.length === 0 ? (
              <div className="no-data">Henüz gider kaydı bulunmamaktadır.</div>
            ) : (
              <table className="masraflar-table">
                <colgroup>
                  <col className="col-dosya" />
                  <col className="col-masraf-turu" />
                  <col className="col-tutar" />
                  <col className="col-tarih" />
                  <col className="col-aciklama" />
                  <col className="col-islemler" />
                </colgroup>
                <thead>
                  <tr>
                    <SortHeader column="dosya">Dosya</SortHeader>
                    <SortHeader column="masrafTuru">Masraf Türü</SortHeader>
                    <SortHeader column="tutar">Tutar</SortHeader>
                    <SortHeader column="tarih">Tarih</SortHeader>
                    <SortHeader column="aciklama">Açıklama</SortHeader>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGiderler.map((masraf) => (
                    <tr key={masraf.id || masraf.Id}>
                      <td>
                        <button
                          className="link-button"
                          onClick={() => navigate(`/case-files/${masraf.caseFileId || masraf.CaseFileId}`)}
                        >
                          {getCaseFileName(masraf.caseFileId || masraf.CaseFileId)}
                        </button>
                      </td>
                      <td>{masraf.MasrafTuru || masraf.masrafTuru || '-'}</td>
                      <td>{formatCurrency(masraf.Tutar || masraf.tutar)}</td>
                      <td>{formatDate(masraf.Tarih || masraf.tarih)}</td>
                      <td>{masraf.Aciklama || masraf.aciklama || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          {isAdmin() && (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEdit(masraf)}
                              >
                                Düzenle
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(masraf.id || masraf.Id)}
                              >
                                Sil
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Aylık Gelir (Sadece Admin) */}
      {isAdmin() && (() => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Bu ayın gelirleri
        const buAyGelirler = gelirler.filter(m => {
          const tarih = m.Tarih || m.tarih;
          if (!tarih) return false;
          try {
            const masrafTarihi = new Date(tarih);
            return masrafTarihi.getMonth() === currentMonth && masrafTarihi.getFullYear() === currentYear;
          } catch {
            return false;
          }
        });

        // Bu ayın giderleri
        const buAyGiderler = giderler.filter(m => {
          const tarih = m.Tarih || m.tarih;
          if (!tarih) return false;
          try {
            const masrafTarihi = new Date(tarih);
            return masrafTarihi.getMonth() === currentMonth && masrafTarihi.getFullYear() === currentYear;
          } catch {
            return false;
          }
        });

        // Toplam gelirler
        const toplamGelir = buAyGelirler.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);

        // Toplam giderler
        const toplamGider = buAyGiderler.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);

        // Net aylık gelir
        const netAylikGelir = toplamGelir - toplamGider;

        const ayIsimleri = [
          'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
          'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
        ];

        return (
          <div className="aylik-gelir-section">
            <h2 className="section-title">📊 {ayIsimleri[currentMonth]} {currentYear} Aylık Gelir</h2>
            <div className="aylik-gelir-card">
              <div className="gelir-bilgileri">
                <div className="gelir-item">
                  <span className="gelir-label">Bu Ay Toplam Gelir:</span>
                  <span className="gelir-value gelir-positive">{formatCurrency(toplamGelir)}</span>
                </div>
                <div className="gelir-item">
                  <span className="gelir-label">Bu Ay Toplam Gider:</span>
                  <span className="gelir-value gelir-negative">{formatCurrency(toplamGider)}</span>
                </div>
                <div className="gelir-item gelir-net">
                  <span className="gelir-label">Net Aylık Gelir:</span>
                  <span className={`gelir-value ${netAylikGelir >= 0 ? 'gelir-positive' : 'gelir-negative'}`}>
                    {formatCurrency(netAylikGelir)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Masraflar;
